/**
 * §P.7 fase 2 · Los papeles de una persona del personal.
 *
 * ## Lo que aporta el producto es avisar, no guardar
 *
 * El contrato lo dice donde se define el cuerpo: «lo que aporta el producto no es
 * guardar el papel —eso lo hace una carpeta— sino **avisar antes de que caduque**,
 * que es lo que nadie hace a mano y lo que deja a un club con personal dando clase
 * con el certificado vencido». Por eso `expires_on` es el campo importante de esta
 * pantalla y no un detalle opcional al final.
 *
 * ## «Sin fecha de caducidad» no es «al día»
 *
 * Un certificado del que no sabemos cuándo vence es un certificado del que no
 * sabemos nada, y pintarlo en verde es exactamente cómo un club se queda tranquilo
 * con un papel vencido. Va en gris y con esas palabras.
 *
 * ## El PDF se adjunta al anotar, y sigue siendo opcional · 22-09-2026
 *
 * §S.3 ya está cableado, así que el fichero se puede subir. **Pero lo que aporta el
 * producto no es guardar el papel** —eso lo hace una carpeta— sino avisar antes de que
 * caduque, así que el PDF no es obligatorio: un club que tiene los papeles en un
 * archivador y solo quiere el aviso sigue pudiendo anotar la fecha y ya está.
 *
 * El uso es `document`, que solo admite PDF. Y `GET /files/{id}` de un
 * `certificado_delitos` **solo responde al dueño del club**, con una línea en
 * `audit_log` por cada descarga: es el papel más sensible que guarda la aplicación.
 */
import { useState } from 'react';
import { SubirFichero } from '@/shared/ui/SubirFichero';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import {
  CLASES_DE_DOCUMENTO,
  cuantoQuedaDeDocumento,
  ESTADOS_DE_DOCUMENTO,
  estadoDeDocumento,
  etiquetaDeClaseDeDocumento,
  type ClaseDeDocumento,
  type DocumentoDePersonal,
} from '@/club/ajustes/tipos';

export function DocumentosDePersonal({
  club,
  usuarioId,
  nombre,
  puedeEditar,
  alCerrar,
}: {
  club: string;
  usuarioId: string;
  nombre: string;
  puedeEditar: boolean;
  alCerrar: () => void;
}) {
  const cache = useQueryClient();
  const [anotando, setAnotando] = useState(false);
  const [clase, setClase] = useState<ClaseDeDocumento>('certificado_delitos');
  const [caduca, setCaduca] = useState('');
  const [expedido, setExpedido] = useState('');
  const [referencia, setReferencia] = useState('');
  /** El PDF ya subido y confirmado, si lo hay. Es opcional: ver la cabecera. */
  const [pdf, setPdf] = useState<string | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  const documentos = useQuery({
    queryKey: ['documentos-de-personal', club, usuarioId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .staff({ userId: usuarioId })
        .documents.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as DocumentoDePersonal[];
    },
  });

  const anotar = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .staff({ userId: usuarioId })
        .documents.post({
          kind: clase,
          expires_on: caduca === '' ? null : caduca,
          issued_on: expedido === '' ? null : expedido,
          reference: referencia.trim() === '' ? null : referencia.trim(),
          ...(pdf === null ? {} : { asset_id: pdf }),
        });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setAnotando(false);
      setPdf(null);
      setCaduca('');
      setExpedido('');
      setReferencia('');
      void cache.invalidateQueries({
        queryKey: ['documentos-de-personal', club, usuarioId],
      });
      // El aviso de caducidad de la lista de personal cuenta estos papeles.
      void cache.invalidateQueries({
        queryKey: ['documentos-que-caducan', club],
      });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (anotando) {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          setProblema(null);
          anotar.mutate();
        }}
      >
        <h1>Anotar un papel de {nombre}</h1>

        <div className="rejilla">
          <label>
            Qué papel es
            <select
              value={clase}
              onChange={(e) => setClase(e.target.value as ClaseDeDocumento)}
            >
              {CLASES_DE_DOCUMENTO.map((c) => (
                <option key={c.valor} value={c.valor}>
                  {c.etiqueta}
                  {c.obligatorio ? ' (obligatorio por ley)' : ''}
                </option>
              ))}
            </select>
          </label>

          <label>
            {/* El campo que de verdad importa. Ver la cabecera. */}
            Cuándo caduca
            <input
              type="date"
              value={caduca}
              onChange={(e) => setCaduca(e.target.value)}
            />
          </label>

          <label>
            Cuándo se expidió
            <input
              type="date"
              value={expedido}
              onChange={(e) => setExpedido(e.target.value)}
            />
          </label>

          <label>
            Número o referencia
            <input
              maxLength={100}
              placeholder="Opcional"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
            />
          </label>
        </div>

        {/* El PDF, opcional. Lo que importa de verdad es la fecha de arriba. */}
        {pdf === null ? (
          <SubirFichero
            uso="document"
            club={club}
            clase={clase}
            etiqueta="Adjuntar el PDF (opcional)"
            deshabilitado={!puedeEditar}
            alSubir={(asset) => setPdf(asset.id)}
          />
        ) : (
          <div className="acciones">
            <span className="pastilla pastilla-bien">PDF listo</span>
            <button
              type="button"
              className="btn-plano"
              onClick={() => setPdf(null)}
            >
              Quitarlo
            </button>
          </div>
        )}

        {/*
          Se puede guardar sin fecha, pero se dice lo que se pierde: el aviso. No se
          bloquea porque a veces el papel no caduca, y obligar haría inventarse una.
        */}
        {caduca === '' && (
          <div className="aviso" role="status">
            <strong>Sin fecha de caducidad no podemos avisarte.</strong> Se
            puede guardar así —hay papeles que no caducan— pero entonces esto es
            una carpeta, y lo que aporta Kiap es el aviso antes de que venza.
          </div>
        )}

        <p className="tenue">
          El PDF todavía no se puede adjuntar desde aquí. La fecha y la
          referencia son lo que hace falta para que Kiap avise a tiempo.
        </p>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={anotar.isPending}>
            {anotar.isPending ? 'Guardando…' : 'Anotar el papel'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={anotar.isPending}
            onClick={() => {
              setAnotando(false);
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  return (
    <section>
      <header className="titulo-fila">
        <h1>Papeles de {nombre}</h1>
        <span className="tutor-acciones" style={{ margin: 0 }}>
          {puedeEditar && (
            <button type="button" onClick={() => setAnotando(true)}>
              Anotar un papel
            </button>
          )}
          <button type="button" className="btn-plano" onClick={alCerrar}>
            Volver al personal
          </button>
        </span>
      </header>

      <p className="tenue">
        El que importa es el <strong>certificado de delitos sexuales</strong>:
        obligatorio por ley para todo el que trata con menores, y el club
        responde de tenerlo al día.
      </p>

      {documentos.isPending && (
        <Cargando filas={3} etiqueta="Cargando los papeles" />
      )}

      {documentos.isError && (
        <PanelError
          problema={aProblema(documentos.error)}
          reintentar={() => void documentos.refetch()}
        />
      )}

      {problema && <PanelError problema={problema} />}

      {documentos.data?.length === 0 && (
        <Vacio
          titulo="No hay ningún papel anotado"
          explicacion="Anota al menos el certificado de delitos sexuales con su fecha de caducidad, y Kiap avisará antes de que venza."
          {...(puedeEditar
            ? {
                accion: (
                  <button type="button" onClick={() => setAnotando(true)}>
                    Anotar el primero
                  </button>
                ),
              }
            : {})}
        />
      )}

      {documentos.data && documentos.data.length > 0 && (
        <ul className="papeles">
          {documentos.data.map((d) => {
            const estado = estadoDeDocumento(d);
            const pinta = ESTADOS_DE_DOCUMENTO[estado];

            return (
              <li
                key={d.id}
                className={estado === 'caducado' ? 'fila-fuera' : undefined}
              >
                <div className="aviso-cabeza">
                  <strong>{etiquetaDeClaseDeDocumento(d.kind)}</strong>
                  <span className={`pastilla ${pinta.clase}`}>
                    {pinta.etiqueta}
                  </span>
                  {d.verified_at && (
                    <span className="pastilla pastilla-info">Comprobado</span>
                  )}
                </div>

                <p>{cuantoQuedaDeDocumento(d.days_to_expiry)}</p>

                <p className="tenue">
                  {d.expires_on
                    ? `Caduca el ${fecha(d.expires_on)}`
                    : 'Sin fecha'}
                  {d.issued_on ? ` · expedido el ${fecha(d.issued_on)}` : ''}
                  {d.reference ? ` · nº ${d.reference}` : ''}
                  {d.asset_id ? '' : ' · sin PDF adjunto'}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
